import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Coins, Clock, CheckCircle2, AlertTriangle, Sparkles, Loader2, Wallet, TrendingUp, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * ROI Claim Widget — v2 PER-STAKE engine.
 *
 * Behaviour:
 *  - Header shows AGGREGATE: total unclaimed, total cap, total earned, single Claim All button.
 *  - Below the header → one card per deposit (stake), each with its own:
 *     • principal, 2X/3X/4X cap, earned, pending, progress bar, status badge
 *     • next 24h ROI countdown (per-stake)
 *  - Claim All sweeps pending_roi from every active stake into the wallet.
 *  - When a stake hits its cap → "COMPLETED" badge; other stakes keep running.
 */
export function ROIClaimWidget({ onClaimSuccess, refreshKey }) {
  const [summary, setSummary] = useState(null);   // /wallet/roi/status response
  const [stakesData, setStakesData] = useState(null); // /wallet/stakes response
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [justActivated, setJustActivated] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusRes, stakesRes] = await Promise.all([
        api.get("/wallet/roi/status"),
        api.get("/wallet/stakes"),
      ]);
      setSummary(prev => {
        if (prev && prev.cycle_status === "inactive" && statusRes.data.cycle_status === "active") {
          setJustActivated(true);
          setTimeout(() => setJustActivated(false), 4000);
        }
        return statusRes.data;
      });
      setStakesData(stakesRes.data);

      // AUTO-REBUILD: if the user has a legacy merged stake (created by the
      // initial per-stake migration when their deposits were made BEFORE the
      // feature was deployed), retroactively split it back into individual
      // stake cards using their deposit history. Runs once per browser via
      // localStorage flag so we don't loop.
      const stks = stakesRes.data?.stakes || [];
      const hasLegacy = stks.some(s => s._migrated === true);
      const flagKey = "stakes_rebuilt_v1";
      if (hasLegacy && !localStorage.getItem(flagKey)) {
        try {
          localStorage.setItem(flagKey, "1");
          const rb = await api.post("/wallet/stakes/rebuild");
          if (rb.data?.ok && rb.data.stake_count > stks.length) {
            // Refresh with the new split cards
            const [s2, k2] = await Promise.all([
              api.get("/wallet/roi/status"),
              api.get("/wallet/stakes"),
            ]);
            setSummary(s2.data);
            setStakesData(k2.data);
            toast.success(`Stake cards rebuilt · ${rb.data.stake_count} deposits restored`, {
              description: "Each past deposit now has its own card.",
            });
          }
        } catch (e) {
          // Silent — don't block the UI if rebuild fails
        }
      }
    } catch (e) {
      /* silent — widget hides if inactive */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (refreshKey !== undefined) load();
  }, [refreshKey, load]);

  // Aggregate live countdown — uses soonest-stake board tick
  useEffect(() => {
    if (!summary?.seconds_until_next_board) return;
    let remaining = summary.seconds_until_next_board;
    setCountdown(remaining);
    const t = setInterval(() => {
      remaining = Math.max(0, remaining - 1);
      setCountdown(remaining);
      if (remaining === 0) {
        clearInterval(t);
        load();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [summary?.seconds_until_next_board, load]);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const r = await api.post("/wallet/roi/claim");
      toast.success(`Claimed $${r.data.claimed_amount.toFixed(4)} to wallet`, {
        description: `Wallet balance: $${r.data.new_wallet_balance.toFixed(2)}`,
      });
      await load();
      onClaimSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  const stakes = stakesData?.stakes || [];
  const totalPending = summary?.pending_roi_claim || 0;
  const totalCap = summary?.cycle_cap || 0;
  const totalEarned = summary?.cycle_earnings || 0;
  const totalProgress = summary?.cycle_progress_pct || 0;
  const allCapped = summary?.is_capped;
  const rankLabel = summary?.leadership_rank || "Investor";
  const activeStakes = stakesData?.active_count ?? stakes.filter(s => s.status === "active").length;
  const cappedStakes = stakesData?.capped_count ?? stakes.filter(s => s.status === "capped").length;

  if (loading) {
    return (
      <div className="card-3d p-4 text-center" data-testid="roi-widget-loading">
        <Loader2 size={16} className="inline animate-spin opacity-50" />
      </div>
    );
  }
  if (!summary || summary.cycle_status === "inactive" || (summary.self_deposit ?? 0) <= 0) {
    return null;
  }

  return (
    <div data-testid="roi-claim-widget" className="space-y-3">
      {/* ─── HEADER LABEL ─── */}
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] flex items-center gap-1.5">
          <Layers size={11} />
          My Staking Cards
          <span className="ml-1 mono text-[var(--text-2)]">({stakes.length})</span>
        </div>
        <div className="text-[10px] mono text-[var(--text-3)]">
          Rank: <span style={{ color: "#FFD36A" }}>{rankLabel}</span>
        </div>
      </div>

      {/* ─── AGGREGATE / CLAIM-ALL CARD ─── */}
      <div
        className="card-3d lift-on-hover relative overflow-hidden p-0"
        style={{
          background: allCapped
            ? "linear-gradient(180deg, rgba(239,68,68,0.10) 0%, rgba(8,20,38,0.95) 100%)"
            : "linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(8,20,38,0.95) 100%)",
          border: allCapped
            ? "1px solid rgba(239,68,68,0.40)"
            : "1px solid rgba(34,197,94,0.35)",
          boxShadow: justActivated ? "0 0 32px rgba(34,197,94,0.50)" : undefined,
          transition: "box-shadow 600ms ease-out",
        }}
        data-testid="roi-aggregate-card"
      >
        {/* Status banner */}
        {!allCapped && (
          <div
            className="px-4 py-2 flex items-center justify-center gap-2 border-b"
            style={{
              background: justActivated
                ? "linear-gradient(90deg, rgba(34,197,94,0.20) 0%, rgba(34,197,94,0.08) 100%)"
                : "linear-gradient(90deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.04) 100%)",
              borderBottomColor: "rgba(34,197,94,0.25)",
            }}
            data-testid="staking-active-banner"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: "#22C55E",
                boxShadow: "0 0 8px #22C55E",
                animation: "pulse 1.6s ease-in-out infinite",
              }}
            />
            <span
              className="text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ color: "#86EFAC" }}
            >
              {justActivated ? "Staking Activated!" : `${activeStakes} Active`}
              {cappedStakes > 0 && (
                <span className="ml-2 opacity-70">· {cappedStakes} Completed</span>
              )}
            </span>
          </div>
        )}

        {/* Aggregate header */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: allCapped
                  ? "linear-gradient(135deg, rgba(239,68,68,0.30), rgba(239,68,68,0.10))"
                  : "linear-gradient(135deg, rgba(34,197,94,0.30), rgba(34,197,94,0.10))",
                border: allCapped
                  ? "1px solid rgba(239,68,68,0.40)"
                  : "1px solid rgba(34,197,94,0.40)",
              }}
            >
              {allCapped ? (
                <AlertTriangle size={18} style={{ color: "#FCA5A5" }} />
              ) : (
                <Wallet size={18} style={{ color: "#86EFAC" }} />
              )}
            </div>
            <div>
              <div className="text-sm font-semibold">Total Unclaimed ROI</div>
              <div className="text-[10px] text-[var(--text-3)]">
                Across {stakes.length} stake{stakes.length !== 1 ? "s" : ""}
                {summary?.next_day_roi_amount > 0 && (
                  <> · ~${summary.next_day_roi_amount.toFixed(2)}/day total</>
                )}
              </div>
            </div>
          </div>
          {!allCapped && countdown !== null && (
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.12em] text-[var(--text-3)] flex items-center gap-1">
                <Clock size={9} /> Next tick
              </div>
              <div className="mono text-xs font-semibold" style={{ color: "#FFD36A" }}>
                {formatCountdown(countdown)}
              </div>
            </div>
          )}
        </div>

        {/* BIG aggregate pending number */}
        <div className="px-4 pt-2 pb-3 text-center">
          <div
            className="mono text-3xl font-bold"
            style={{
              color: totalPending > 0 ? "#FFD36A" : "var(--text-2)",
              textShadow: totalPending > 0 ? "0 0 12px rgba(255,211,106,0.30)" : "none",
            }}
            data-testid="roi-pending-amount"
          >
            ${totalPending.toFixed(4)}
          </div>
        </div>

        {/* Aggregate cap progress */}
        <div className="px-4 pb-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-[var(--text-3)]">
              Total Earned: ${totalEarned.toFixed(2)} / ${totalCap.toFixed(0)}
            </span>
            <span className="mono" style={{ color: allCapped ? "#FCA5A5" : "#FFD36A" }}>
              {totalProgress.toFixed(1)}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, totalProgress)}%`,
                background: allCapped
                  ? "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)"
                  : "linear-gradient(90deg, #FFD36A 0%, #F3BA2F 100%)",
                transition: "width 320ms ease-out",
              }}
            />
          </div>
        </div>

        {/* Claim-All CTA */}
        <div className="px-4 pb-4">
          {allCapped ? (
            <div
              className="rounded-lg px-3 py-2.5 text-center text-[11px] font-semibold"
              style={{
                background: "rgba(239,68,68,0.10)",
                border: "1px solid rgba(239,68,68,0.30)",
                color: "#FCA5A5",
              }}
              data-testid="roi-capped-banner"
            >
              All stakes capped — deposit again to start a fresh stake card
            </div>
          ) : totalPending > 0 ? (
            <Button
              onClick={handleClaim}
              disabled={claiming}
              className="w-full h-11 font-semibold text-black"
              style={{
                background: "linear-gradient(135deg, #FFE5A1 0%, #FFD36A 50%, #F3BA2F 100%)",
                boxShadow: "0 4px 16px rgba(255,211,106,0.30)",
              }}
              data-testid="roi-claim-button"
            >
              {claiming ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" /> Claiming…
                </>
              ) : (
                <>
                  <Sparkles size={16} className="mr-2" /> Claim All ${totalPending.toFixed(4)}
                </>
              )}
            </Button>
          ) : (
            <div
              className="rounded-lg px-3 py-2.5 text-center text-[11px]"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "var(--text-3)",
              }}
            >
              <CheckCircle2 size={11} className="inline mr-1" />
              Next ROI board in {countdown !== null ? formatCountdown(countdown) : "..."}
            </div>
          )}
        </div>
      </div>

      {/* ─── PER-STAKE GRID ─── */}
      {stakes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="stake-grid">
          {stakes.map((s, idx) => (
            <StakeCard key={s.id} stake={s} index={stakes.length - idx} />
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Per-Stake card
// ────────────────────────────────────────────────────────────────
function StakeCard({ stake, index }) {
  const isCapped = stake.status === "capped";
  const principal = Number(stake.principal || 0);
  const cap = Number(stake.cap_amount || 0);
  const earned = Number(stake.earned || 0);
  const pending = Number(stake.pending_roi || 0);
  const totalGain = earned + pending;
  const progress = Math.min(100, Number(stake.progress_pct || 0));
  const dailyPct = Number(stake.daily_roi_pct || 1);
  const nextDay = Number(stake.next_day_roi_amount || 0);
  const capX = Number(stake.cap_x || 2);

  // Per-stake countdown — using its own last_roi_accrued_at
  const secs = useStakeCountdown(stake.last_roi_accrued_at, !isCapped);

  // Compute date label
  const dateLabel = useMemo(() => {
    if (!stake.created_at) return "";
    try {
      return new Date(stake.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  }, [stake.created_at]);

  return (
    <div
      className="card-3d relative overflow-hidden p-0"
      style={{
        background: isCapped
          ? "linear-gradient(180deg, rgba(148,163,184,0.06) 0%, rgba(8,20,38,0.95) 100%)"
          : "linear-gradient(180deg, rgba(34,197,94,0.06) 0%, rgba(8,20,38,0.95) 100%)",
        border: isCapped
          ? "1px solid rgba(148,163,184,0.30)"
          : "1px solid rgba(255,211,106,0.25)",
      }}
      data-testid={`stake-card-${stake.id}`}
    >
      {/* Top strip — index # + status pill */}
      <div className="px-3 pt-2.5 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="mono text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{
              background: "rgba(255,211,106,0.15)",
              color: "#FFD36A",
              border: "1px solid rgba(255,211,106,0.30)",
            }}
          >
            #{index}
          </span>
          <span className="text-[10px] text-[var(--text-3)]">{stake.package_name || "Stake"}</span>
        </div>
        <span
          className="text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
          style={{
            background: isCapped ? "rgba(148,163,184,0.15)" : "rgba(34,197,94,0.15)",
            color: isCapped ? "#CBD5E1" : "#86EFAC",
            border: `1px solid ${isCapped ? "rgba(148,163,184,0.35)" : "rgba(34,197,94,0.35)"}`,
          }}
        >
          {isCapped ? "Completed" : "Active"}
        </span>
      </div>

      {/* Principal + Cap row */}
      <div className="px-3 pb-2 grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-[0.12em] text-[var(--text-3)]">Principal</div>
          <div className="mono text-base font-bold" style={{ color: "var(--text-1)" }}>
            ${principal.toFixed(2)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-[0.12em] text-[var(--text-3)]">
            {capX}X Cap
          </div>
          <div className="mono text-base font-bold" style={{ color: "#FFD36A" }}>
            ${cap.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Earned + Pending */}
      <div className="px-3 pb-2 grid grid-cols-2 gap-2">
        <div
          className="rounded-md px-2 py-1.5"
          style={{
            background: "rgba(34,197,94,0.06)",
            border: "1px solid rgba(34,197,94,0.18)",
          }}
        >
          <div className="text-[9px] text-[var(--text-3)] uppercase tracking-[0.10em]">Earned</div>
          <div className="mono text-sm font-semibold" style={{ color: "#86EFAC" }}>
            ${earned.toFixed(4)}
          </div>
        </div>
        <div
          className="rounded-md px-2 py-1.5"
          style={{
            background: pending > 0 ? "rgba(255,211,106,0.08)" : "rgba(255,255,255,0.03)",
            border: pending > 0 ? "1px solid rgba(255,211,106,0.25)" : "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="text-[9px] text-[var(--text-3)] uppercase tracking-[0.10em]">Pending</div>
          <div className="mono text-sm font-semibold" style={{ color: pending > 0 ? "#FFD36A" : "var(--text-2)" }}>
            ${pending.toFixed(4)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-3 pb-2">
        <div className="flex items-center justify-between text-[9px] mb-1">
          <span className="text-[var(--text-3)]">
            ${totalGain.toFixed(2)} / ${cap.toFixed(0)}
          </span>
          <span className="mono" style={{ color: isCapped ? "#CBD5E1" : "#FFD36A" }}>
            {progress.toFixed(1)}%
          </span>
        </div>
        <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress}%`,
              background: isCapped
                ? "linear-gradient(90deg, #94A3B8 0%, #64748B 100%)"
                : "linear-gradient(90deg, #FFD36A 0%, #F3BA2F 100%)",
              transition: "width 320ms ease-out",
            }}
          />
        </div>
      </div>

      {/* Footer — rate + countdown / date */}
      <div
        className="px-3 py-2 flex items-center justify-between text-[10px] border-t"
        style={{ borderTopColor: "rgba(255,255,255,0.06)" }}
      >
        <span className="text-[var(--text-3)] flex items-center gap-1">
          <TrendingUp size={10} />
          <span className="mono">{dailyPct.toFixed(2)}%/day</span>
          {!isCapped && nextDay > 0 && (
            <span className="ml-1 opacity-70">≈ ${nextDay.toFixed(2)}</span>
          )}
        </span>
        <span className="text-[var(--text-3)]">
          {isCapped ? (
            <span className="opacity-80">{dateLabel}</span>
          ) : secs !== null ? (
            <span className="flex items-center gap-1" style={{ color: "#FFD36A" }}>
              <Clock size={10} />
              <span className="mono">{formatCountdown(secs)}</span>
            </span>
          ) : (
            <span className="opacity-80">{dateLabel}</span>
          )}
        </span>
      </div>
    </div>
  );
}

// Hook: per-stake 24h countdown to next ROI board
function useStakeCountdown(lastIso, active) {
  const [secs, setSecs] = useState(null);

  useEffect(() => {
    if (!active || !lastIso) {
      setSecs(null);
      return;
    }
    const compute = () => {
      const last = new Date(lastIso).getTime();
      if (Number.isNaN(last)) return null;
      const elapsed = (Date.now() - last) / 1000;
      const remaining = Math.max(0, 86400 - Math.floor(elapsed % 86400));
      return remaining;
    };
    setSecs(compute());
    const t = setInterval(() => {
      const r = compute();
      setSecs(r);
    }, 1000);
    return () => clearInterval(t);
  }, [lastIso, active]);

  return secs;
}

function formatCountdown(seconds) {
  if (seconds == null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
